/**
 * Which theme applies, as a pure function — requirements 6.4 and 6.5.
 *
 * Pulled out of the DOM on purpose. The interesting behaviour is a three-input decision table, and a
 * table is worth testing directly rather than through a jsdom `matchMedia` stub that proves the stub
 * works. `theme/script.ts` serialises the same table into the blocking inline script, and
 * `theme/current.ts` calls it from the browser, so there is one answer rather than three.
 *
 * ## The precedence, and where the two requirements meet
 *
 * Requirement 6.5 says the dashboard is light by default with a dark toggle that persists.
 * The design adds a fallback to `prefers-color-scheme` for a visitor who has not toggled anything.
 * Both hold, in this order:
 *
 * 1. A stored preference, if it names a theme this system has. That is what "persists across
 *    sessions" means, and it wins over the operating system because it is the more specific signal.
 * 2. `prefers-color-scheme: dark`, for someone who has expressed a system-wide preference and never
 *    touched the toggle.
 * 3. The app's declared default. Light for the dashboard, dark for the site.
 *
 * The site passes `lockedTheme` instead, because it has no toggle: it is the dark brand theme and a
 * stale `localStorage` entry from the dashboard's origin must not be able to lighten it.
 */
import { themeClasses } from '../tokens/themes.js';
export const themeStorageKey = 'apuestapr:theme';
const isThemeName = (value) => value === 'light' || value === 'dark';
export const resolveTheme = ({ stored, prefersDark = false, fallback }) => {
    if (isThemeName(stored))
        return stored;
    if (prefersDark)
        return 'dark';
    return fallback;
};
/** Every class this system might have put on `<html>`, so applying one can remove the others. */
export const allThemeClasses = Object.values(themeClasses);
//# sourceMappingURL=resolve.js.map